import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  ActivityIndicator,
  FlatList,
  Platform,
  Pressable,
  StyleSheet,
  TextInput,
  View,
  useWindowDimensions,
  ScrollView,
} from 'react-native';
import { useFocusEffect, useNavigation } from '@react-navigation/native';
import { BottomSheetModal } from '@gorhom/bottom-sheet';
import MaterialCommunityIcons from '@react-native-vector-icons/material-design-icons/static';
import Ionicons from '@react-native-vector-icons/ionicons/static';
import Feather from '@react-native-vector-icons/feather/static';
import FontAwesome6 from '@react-native-vector-icons/fontawesome6/static';
import FastImage from '@d11/react-native-fast-image';

// Use the web-style card implementation for sidebar/tablet-landscape too.
// Import explicitly so native tablet doesn't resolve to DemoProductCard.tsx.
import DemoProductCard from './DemoProductCard.web';
import DemoProductListNative from './DemoProductListNative';
import AppSearchBarWeb from '../../../components/Reusables/AppSearchBar.web';
import { useTheme, GTFonts, useLayoutSize } from '../../../theme';
import {
  getProductGridNumColumns,
  shouldShowTabletSidebarLayoutSetting,
} from '../../../utils/layoutUtils';
import {
  fs,
  s,
  v,
  iconSize,
  borderWidth,
  forceHttps,
} from '../../../utils/libs';
import Typography from '../../../components/Reusables/Typography';
import CustomBottomSheet from '../../../components/Reusables/bottomSheet/CustomBottomSheet';
import ProductDetailBottomSheet, {
  type ProductDetailCartBridge,
} from '../../../components/ProductDetailBottomSheet';
import WebHeaderBar from '../../../components/web/WebHeaderBar';
import useWebHeader from '../../../components/web/useWebHeader';
import HeaderIconButton from '../../../components/Reusables/headers/HeaderIconButton';
import { ExtrasContext } from '../../../Context/ExtrasContext';
import {
  useDemoCart,
  useDemoProducts,
  type DemoProductItem,
} from '../../../hooks/demo';
import { useDetectedCountry } from '../../../hooks/useDetectedCountry';
import { AppSnackbar } from '../../../services/AppSnackbar';
import { confirmQuitDemoAndGoToOnboarding } from '../../../navigation/demo/confirmQuitDemo';
import {
  demoProductInStock,
  toDemoSheetProduct,
} from '../../../utils/demoProductAdapter';
import {
  getPriceRangeDisplay,
  isVariantProduct,
} from '../../../utils/variantHelpers';
import type { ProductObject } from '../../../redux/productSlice';

const SMALL_SCREEN_BREAKPOINT = 768;

interface DemoProductListWebProps {
  onCartCountChange?: (count: number) => void;
  searchQuery?: string;
}

const DemoProductListWeb: React.FC<DemoProductListWebProps> = ({
  onCartCountChange,
  searchQuery: searchQueryProp = '',
}) => {
  const { width } = useWindowDimensions();

  if (width < SMALL_SCREEN_BREAKPOINT) {
    return (
      <DemoProductListNative
        onCartCountChange={onCartCountChange}
        searchQuery={searchQueryProp}
      />
    );
  }

  return (
    <DemoProductListWebInner
      onCartCountChange={onCartCountChange}
      searchQueryProp={searchQueryProp}
    />
  );
};

const DemoProductListWebInner: React.FC<{
  onCartCountChange?: (count: number) => void;
  searchQueryProp: string;
}> = ({ onCartCountChange, searchQueryProp }) => {
  const navigation = useNavigation();
  const { width, height, useSidebarNav: isSidebar } = useLayoutSize();
  const showTabletSidebarLayoutSetting = shouldShowTabletSidebarLayoutSetting(
    width,
    height,
  );
  const isPortraitSidebarMode = showTabletSidebarLayoutSetting && isSidebar;
  const { theme } = useTheme();
  const { isRowLayout, networkAvailableGlobal } = useContext(ExtrasContext);
  const { currency } = useDetectedCountry();

  // In sidebar/tablet-landscape, always use grid/column cards (web standard).
  const effectiveIsRowLayout = isSidebar ? false : isRowLayout;

  const subcategoryPickerSheetRef = useRef<BottomSheetModal>(null as any);
  const subcategoryHeaderListRef = useRef<FlatList<string>>(null);
  const productDetailBottomSheetRef = useRef<BottomSheetModal>(null as any);
  const searchInputRef = useRef<TextInput>(null);

  const [localSearch, setLocalSearch] = useState(searchQueryProp);
  const [subcategory, setSubcategory] = useState('AllProducts');
  const [subcategoryPickerOpen, setSubcategoryPickerOpen] = useState(false);
  const [currentTime, setCurrentTime] = useState(new Date());
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [selectedSheetProduct, setSelectedSheetProduct] = useState<any | null>(
    null,
  );

  const { products, isLoading, error, getCategories, refetch } =
    useDemoProducts();
  const {
    cartItems,
    cartCount,
    addToCart,
    removeFromCart,
    updateCartItemQuantity,
    updateCartItemMass,
    setDemoCartUnitMass,
    setDemoCartItemQuantity,
    refetch: refetchDemoCart,
  } = useDemoCart();

  const productsById = useMemo(() => {
    const map = new Map<string, DemoProductItem>();
    products.forEach(p => map.set(p._id, p));
    return map;
  }, [products]);

  useEffect(() => {
    onCartCountChange?.(cartCount);
  }, [cartCount, onCartCountChange]);

  useFocusEffect(
    useCallback(() => {
      void refetchDemoCart();
    }, [refetchDemoCart]),
  );

  const allSubCategories = useMemo(() => {
    const categories = getCategories();
    return ['AllProducts', ...categories];
  }, [getCategories]);

  const filteredProducts = useMemo(() => {
    const query = localSearch.trim().toLowerCase();
    let baseData =
      subcategory === 'AllProducts'
        ? products
        : products.filter(item => item.bp_product_category === subcategory);

    if (query.length > 1) {
      baseData = baseData.filter(
        item =>
          item.bp_product_name.toLowerCase().includes(query) ||
          item.bp_product_barcode.includes(localSearch) ||
          item.bp_product_category.toLowerCase().includes(query),
      );
    }

    return baseData;
  }, [products, subcategory, localSearch]);

  const highlightedProductId = useMemo(() => {
    const first = filteredProducts.find(
      p =>
        p.bp_product_price_type !== 'unit' &&
        !isVariantProduct(p as unknown as ProductObject) &&
        demoProductInStock(p),
    );
    return first?._id ?? null;
  }, [filteredProducts]);

  const categoryCounts = useMemo(() => {
    const map = new Map<string, number>();
    for (const p of products) {
      const sub = String(p?.bp_product_category || '');
      if (!sub) continue;
      map.set(sub, (map.get(sub) || 0) + 1);
    }
    return { map, allCount: products.length };
  }, [products]);

  const numColumns = useMemo(() => getProductGridNumColumns(width), [width]);

  const styles = useMemo(
    () => createStyles(theme, width, isPortraitSidebarMode),
    [isPortraitSidebarMode, theme, width],
  );

  const fetchProductsBySubCategory = useCallback((value: string) => {
    setSubcategory(value);
  }, []);

  const getCartMetaForProduct = useCallback(
    (productId: string, variantId?: string | null) => {
      const cartItem = cartItems.find(
        item =>
          item.product_id === productId &&
          (item.variant_id ?? '') === (variantId ?? ''),
      );
      return {
        quantityInCart: cartItem?.quantity ?? 0,
        massInCart: cartItem?.mass ?? 0,
        cartItemId: cartItem?._id,
      };
    },
    [cartItems],
  );

  const handleProductPress = useCallback((product: DemoProductItem) => {
    setSelectedSheetProduct(toDemoSheetProduct(product));
    requestAnimationFrame(() => {
      productDetailBottomSheetRef.current?.present();
    });
  }, []);

  const handleSubproductQuantity = useCallback(
    (sheetProduct: any) => {
      const product = productsById.get(sheetProduct?.productID);
      if (!product) return;
      const variantId = sheetProduct?.variantId;
      const { cartItemId, quantityInCart } = getCartMetaForProduct(
        product._id,
        variantId,
      );
      if (!cartItemId) return;
      void updateCartItemQuantity(
        cartItemId,
        Math.max(0, quantityInCart - 1),
      );
    },
    [getCartMetaForProduct, productsById, updateCartItemQuantity],
  );

  const cartBridge = useMemo<ProductDetailCartBridge>(
    () => ({
      cartItems: cartItems.map(item => ({
        productID: item.product_id,
        variantId: item.variant_id,
        Quantity: item.quantity,
        mass: item.mass,
      })),
      onAdd: (payload: any) => {
        const product = productsById.get(payload?.productID);
        if (!product) return;
        const variantId = payload?.variantId;
        if (payload?.price_type === 'unit') {
          setDemoCartUnitMass(product, Number(payload?.mass ?? 0), {
            variantId,
          });
          return;
        }
        void addToCart(product, 1, 0, { variantId });
      },
      onSetQuantity: ({ productID, variantId, quantity }) => {
        const product = productsById.get(productID);
        if (!product) return;
        setDemoCartItemQuantity(product, quantity, { variantId });
      },
      onRemove: ({ productID, variantId }) => {
        const { cartItemId } = getCartMetaForProduct(productID, variantId);
        if (cartItemId) {
          void removeFromCart(cartItemId);
        }
      },
    }),
    [
      addToCart,
      cartItems,
      getCartMetaForProduct,
      productsById,
      removeFromCart,
      setDemoCartItemQuantity,
      setDemoCartUnitMass,
    ],
  );

  const isUnitType = useCallback(
    (product: DemoProductItem) => product.bp_product_price_type === 'unit',
    [],
  );

  const handleAdd = useCallback(
    async (product: DemoProductItem) => {
      if (isVariantProduct(product as unknown as ProductObject)) {
        handleProductPress(product);
        return;
      }
      const { cartItemId, quantityInCart, massInCart } = getCartMetaForProduct(
        product._id,
      );

      if (!cartItemId) {
        await addToCart(
          product,
          isUnitType(product) ? 0 : 1,
          isUnitType(product) ? 1 : 0,
        );
        return;
      }

      if (isUnitType(product)) {
        await updateCartItemMass(cartItemId, massInCart + 1);
      } else {
        await updateCartItemQuantity(cartItemId, quantityInCart + 1);
      }
    },
    [
      addToCart,
      getCartMetaForProduct,
      handleProductPress,
      isUnitType,
      updateCartItemMass,
      updateCartItemQuantity,
    ],
  );

  const handleRemove = useCallback(
    async (product: DemoProductItem) => {
      const { cartItemId, quantityInCart, massInCart } = getCartMetaForProduct(
        product._id,
      );
      if (!cartItemId) return;

      if (isUnitType(product)) {
        await updateCartItemMass(cartItemId, Math.max(0, massInCart - 1));
      } else {
        await updateCartItemQuantity(
          cartItemId,
          Math.max(0, quantityInCart - 1),
        );
      }
    },
    [
      getCartMetaForProduct,
      isUnitType,
      updateCartItemMass,
      updateCartItemQuantity,
    ],
  );

  const handleSetQuantity = useCallback(
    (product: DemoProductItem, quantity: number) => {
      setDemoCartItemQuantity(product, quantity);
    },
    [setDemoCartItemQuantity],
  );

  const handleSetMass = useCallback(
    (product: DemoProductItem, mass: number) => {
      setDemoCartUnitMass(product, mass);
    },
    [setDemoCartUnitMass],
  );

  const handleSearchChange = useCallback((text: string) => {
    setLocalSearch(text);
  }, []);

  const handleSearchSubmit = useCallback(() => {
    // Search is live via localSearch
  }, []);

  const handleClearSearch = useCallback(() => {
    setLocalSearch('');
    searchInputRef.current?.focus();
  }, []);

  const handleDemoBarcodeScan = useCallback(() => {
    AppSnackbar.show(
      'Scan-to-sell is not available in demo mode. Create a BizPlus POS account to use this feature.',
      'info',
    );
  }, []);

  const handleDemoFilter = useCallback(() => {
    AppSnackbar.show(
      'Product filtering is not available in demo mode. Create a BizPlus POS account to use this feature.',
      'info',
    );
  }, []);

  const handleManualRefresh = useCallback(async () => {
    setIsRefreshing(true);
    try {
      await refetch();
    } finally {
      setTimeout(() => setIsRefreshing(false), 500);
    }
  }, [refetch]);

  useEffect(() => {
    setCurrentTime(new Date());
    const intervalId = setInterval(() => setCurrentTime(new Date()), 1000);
    return () => clearInterval(intervalId);
  }, []);

  const onViewableItemsChanged = useRef(({ viewableItems }: any) => {
    try {
      if (!Array.isArray(viewableItems) || viewableItems.length === 0) return;
      const items = viewableItems.map((v: any) => v?.item).filter(Boolean);
      const sources = items
        .map((p: any) => String(p?.bp_product_image || ''))
        .filter((u: string) => !!u)
        .map((uri: string) => ({
          uri: forceHttps(uri),
          priority: FastImage.priority.high,
        }));
      if (sources.length) FastImage.preload(sources as any);
    } catch {}
  }).current;

  useEffect(() => {
    const initial = filteredProducts.slice(0, 60);
    const sources = initial
      .map((p: DemoProductItem) => String(p?.bp_product_image || ''))
      .filter(u => !!u)
      .map(uri => ({
        uri: forceHttps(uri),
        priority: FastImage.priority.high,
      }));
    if (sources.length) FastImage.preload(sources as any);
  }, [filteredProducts]);

  const handleHeaderBack = useCallback(() => {
    confirmQuitDemoAndGoToOnboarding(navigation);
  }, [navigation]);

  const webHeaderActive = isSidebar;

  const headerNode = useMemo(() => {
    if (!webHeaderActive) return null;

    return (
      <WebHeaderBar
        onBack={handleHeaderBack}
        rightSlot={
          <View style={styles.topBarRight}>
            <HeaderIconButton
              tooltip="Sort products"
              onPress={handleDemoFilter}
              style={({ hovered }: any) => [
                styles.topBarAction,
                hovered && styles.topBarActionHovered,
              ]}>
              <Ionicons
                name="options-outline"
                size={iconSize(20)}
                color={theme.colors.textInverse}
              />
            </HeaderIconButton>

            <HeaderIconButton
              tooltip="Refresh products"
              onPress={handleManualRefresh}
              disabled={isRefreshing}
              style={({ hovered }: any) => [
                styles.topBarAction,
                hovered && styles.topBarActionHovered,
              ]}>
              <MaterialCommunityIcons
                name="refresh"
                size={iconSize(20)}
                color={theme.colors.textInverse}
                style={isRefreshing ? { opacity: 0.5 } : undefined}
              />
            </HeaderIconButton>

            <View
              style={[
                styles.networkIndicator,
                networkAvailableGlobal
                  ? styles.networkOnline
                  : styles.networkOffline,
              ]}>
              <Ionicons
                name={networkAvailableGlobal ? 'wifi' : 'wifi-outline'}
                size={iconSize(16)}
                color={networkAvailableGlobal ? theme.colors.successLight : theme.colors.error}
              />
              {isPortraitSidebarMode ? null : (
                <Typography style={styles.networkText}>
                  {networkAvailableGlobal ? 'Online' : 'Offline'}
                </Typography>
              )}
            </View>

            <View style={styles.dateTimeContainer}>
              <Feather
                name="calendar"
                size={iconSize(14)}
                color="rgba(255, 255, 255, 0.85)"
              />
              <Typography style={styles.dateTimeText}>
                {currentTime.toLocaleDateString('en-US', {
                  weekday: 'short',
                  month: 'short',
                  day: 'numeric',
                  year: 'numeric',
                })}
              </Typography>
            </View>
            <View style={styles.dateTimeContainer}>
              <Feather
                name="clock"
                size={iconSize(14)}
                color="rgba(255, 255, 255, 0.85)"
              />
              <Typography style={[styles.dateTimeText, styles.timeText]}>
                {currentTime.toLocaleTimeString('en-US', {
                  hour: '2-digit',
                  minute: '2-digit',
                  second: '2-digit',
                })}
              </Typography>
            </View>
          </View>
        }>
        <View style={styles.searchSlot}>
          <AppSearchBarWeb
            placeholder="Enter product name"
            value={localSearch}
            onChangeText={handleSearchChange}
            onSubmit={handleSearchSubmit}
            onClear={handleClearSearch}
            showLeadingIcon={true}
            showBarcodeScan={true}
            height={v(50)}
            width={
              isPortraitSidebarMode ? width * 0.46 : width * 0.32
            }
            onBarcodeScan={handleDemoBarcodeScan}
            inputRef={searchInputRef}
            containerStyle={styles.searchContainerExpanded}
          />
        </View>
      </WebHeaderBar>
    );
  }, [
    webHeaderActive,
    styles,
    handleHeaderBack,
    localSearch,
    handleSearchChange,
    handleSearchSubmit,
    handleClearSearch,
    handleDemoBarcodeScan,
    handleDemoFilter,
    handleManualRefresh,
    isRefreshing,
    networkAvailableGlobal,
    isPortraitSidebarMode,
    currentTime,
  ]);

  useWebHeader(headerNode, webHeaderActive);

  const renderProductItem = useCallback(
    ({ item }: { item: DemoProductItem }) => {
      const { quantityInCart, massInCart } = getCartMetaForProduct(item._id);
      const asProduct = item as unknown as ProductObject;
      const hasVariants = isVariantProduct(asProduct);
      return (
        <View
          style={[
            styles.productItemWrapper,
            { width: `${100 / numColumns}%` },
          ]}>
          <DemoProductCard
            product={item}
            quantityInCart={quantityInCart}
            massInCart={massInCart}
            isRowLayout={effectiveIsRowLayout}
            currency={currency}
            highlightAddButton={highlightedProductId === item._id}
            hasVariants={hasVariants}
            variantCount={item.bp_product_variants?.length ?? 0}
            priceRangeText={
              hasVariants ? getPriceRangeDisplay(asProduct) : undefined
            }
            onProductPress={handleProductPress}
            onAdd={handleAdd}
            onRemove={handleRemove}
            onSetQuantity={handleSetQuantity}
            onSetMass={handleSetMass}
          />
        </View>
      );
    },
    [
      getCartMetaForProduct,
      numColumns,
      effectiveIsRowLayout,
      currency,
      handleAdd,
      handleRemove,
      handleSetQuantity,
      handleSetMass,
      handleProductPress,
      highlightedProductId,
      styles,
    ],
  );

  const renderCategoryPill = useCallback(
    ({ item }: { item: string }) => {
      const isActive = subcategory === item;
      const label = item === 'AllProducts' ? 'All' : item;
      const count =
        item === 'AllProducts'
          ? categoryCounts.allCount
          : categoryCounts.map.get(item) || 0;

      return (
        <Pressable
          onPress={() => fetchProductsBySubCategory(item)}
          style={({ hovered }: any) => [
            styles.categoryPill,
            isActive && styles.categoryPillActive,
            hovered && !isActive && styles.categoryPillHovered,
          ]}>
          <Typography
            style={[
              styles.categoryLabel,
              isActive && styles.categoryLabelActive,
            ]}>
            {label}
          </Typography>
          <View
            style={[
              styles.categoryCountBadge,
              isActive && styles.categoryCountBadgeActive,
            ]}>
            <Typography
              style={[
                styles.categoryCount,
                isActive && styles.categoryCountActive,
              ]}>
              {count}
            </Typography>
          </View>
        </Pressable>
      );
    },
    [subcategory, fetchProductsBySubCategory, styles, categoryCounts],
  );

  const handlePickSubcategory = useCallback(
    (name: string) => {
      setSubcategoryPickerOpen(false);
      const index = allSubCategories.findIndex(item => item === name);
      if (index >= 0) {
        try {
          subcategoryHeaderListRef.current?.scrollToIndex({
            index,
            animated: true,
            viewPosition: 0.5,
          });
        } catch {}
      }
      fetchProductsBySubCategory(name);
    },
    [allSubCategories, fetchProductsBySubCategory],
  );

  const renderPickerItem = useCallback(
    ({ item }: { item: string }) => {
      const isActive = item === subcategory;
      return (
        <Pressable
          onPress={() => handlePickSubcategory(item)}
          style={[
            styles.pickerItem,
            isActive ? styles.pickerItemActive : null,
          ]}>
          <MaterialCommunityIcons
            name={isActive ? 'radiobox-marked' : 'radiobox-blank'}
            size={iconSize(18)}
            color={isActive ? theme.colors.success : theme.colors.text}
          />
          <Typography
            style={[
              styles.pickerLabel,
              isActive ? styles.pickerLabelActive : null,
            ]}>
            {item === 'AllProducts' ? 'All' : item}
          </Typography>
        </Pressable>
      );
    },
    [
      subcategory,
      handlePickSubcategory,
      styles,
      theme.colors.success,
      theme.colors.text,
    ],
  );

  useEffect(() => {
    if (subcategoryPickerOpen) subcategoryPickerSheetRef.current?.present();
    else subcategoryPickerSheetRef.current?.dismiss();
  }, [subcategoryPickerOpen]);

  const keyExtractor = useCallback((item: DemoProductItem) => item._id, []);
  const keyTopExtractor = useCallback(
    (item: string, i: number) => `${item}-${i}`,
    [],
  );

  const renderFooter = useCallback(() => {
    if (isLoading) {
      return (
        <View style={styles.loadingFooter}>
          <ActivityIndicator size="large" color={theme.colors.primary} />
        </View>
      );
    }

    if (filteredProducts.length === 0) {
      return (
        <View style={styles.emptyContainer}>
          <MaterialCommunityIcons
            name="package-variant"
            size={iconSize(64)}
            color={theme.colors.textTertiary}
          />
          <Typography style={styles.emptyText}>No products found</Typography>
          <Typography style={styles.emptySubtext}>
            {localSearch.trim()
              ? 'Try a different search term'
              : `No products in "${subcategory}"`}
          </Typography>
        </View>
      );
    }
    return null;
  }, [
    isLoading,
    filteredProducts.length,
    localSearch,
    subcategory,
    styles,
    theme,
  ]);

  return (
    <View style={styles.container}>
      <View style={styles.categoriesSection}>
        <Pressable
          onPress={() => setSubcategoryPickerOpen(true)}
          style={({ hovered }: any) => [
            styles.categoryToggle,
            hovered && styles.categoryToggleHovered,
          ]}>
          <Feather
            name="list"
            size={iconSize(18)}
            color={theme.colors.textSecondary}
          />
        </Pressable>

        <ScrollView
          horizontal
          showsHorizontalScrollIndicator={false}
          contentContainerStyle={styles.categoriesContent}>
          {allSubCategories.map((item, index) => (
            <React.Fragment key={`${item}-${index}`}>
              {renderCategoryPill({ item })}
            </React.Fragment>
          ))}
        </ScrollView>
      </View>

      <View style={styles.productsArea}>
        {isLoading ? (
          <View style={styles.centerState}>
            <ActivityIndicator size="large" color={theme.colors.primary} />
          </View>
        ) : error ? (
          <View style={styles.centerState}>
            <Typography style={styles.errorText}>{error}</Typography>
          </View>
        ) : (
          <FlatList
            data={filteredProducts}
            renderItem={renderProductItem}
            keyExtractor={keyExtractor}
            numColumns={numColumns}
            key={numColumns}
            style={{ width: '100%' }}
            contentContainerStyle={styles.gridContent}
            initialNumToRender={16}
            onViewableItemsChanged={onViewableItemsChanged}
            viewabilityConfig={{ itemVisiblePercentThreshold: 60 }}
            showsVerticalScrollIndicator={true}
            ListFooterComponent={renderFooter}
          />
        )}
      </View>

      {cartCount > 0 && (
        <Pressable
          onPress={() => (navigation as any).replace('DemoCart')}
          style={({ pressed }) => [
            styles.floatingCartBtn,
            pressed && styles.floatingCartBtnPressed,
          ]}>
          <View style={styles.floatingCartBadge}>
            <Typography style={styles.floatingCartBadgeText}>
              {cartCount}
            </Typography>
          </View>
          <FontAwesome6
            name="basket-shopping"
            iconStyle="solid"
            size={iconSize(22)}
            color={theme.colors.surface}
          />
        </Pressable>
      )}

      <CustomBottomSheet
        bottomSheetModalRef={subcategoryPickerSheetRef}
        snapPoints={['70%']}
        enableDynamicSizing={false}
        constrainWidth={true}
        handleSheetChanges={index => {
          if (index === -1) setSubcategoryPickerOpen(false);
        }}
        header={
          <Typography style={styles.bottomSheetTitle}>
            Select Category
          </Typography>
        }>
        <View style={styles.pickerBody}>
          <FlatList
            data={allSubCategories}
            keyExtractor={keyTopExtractor}
            renderItem={renderPickerItem}
            numColumns={2}
            columnWrapperStyle={styles.pickerRow}
            contentContainerStyle={styles.pickerListContent}
            showsHorizontalScrollIndicator={false}
          />
        </View>
      </CustomBottomSheet>
      <ProductDetailBottomSheet
        bottomSheetRef={productDetailBottomSheetRef}
        product={selectedSheetProduct}
        handleSubproductQuantity={handleSubproductQuantity}
        cartBridge={cartBridge}
      />
    </View>
  );
};

export default DemoProductListWeb;

const createStyles = (
  theme: any,
  screenWidth: number,
  isPortraitSidebarMode: boolean,
) => {
  const isLarge = screenWidth >= 1024;
  const isMedium = screenWidth >= 768;
  const isTabletCompact = isMedium && !isLarge;
  const colors = theme.colors;

  return StyleSheet.create({
    container: {
      flex: 1,
      flexDirection: 'column',
      backgroundColor: colors.backgroundGray100,
    },

    topBar: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'space-between',
      paddingHorizontal: s(8),
      backgroundColor: theme.colors.primaryDark,
    },
    topBarLeft: {
      flex: 1,
      maxWidth: isTabletCompact ? s(660) : s(620),
      flexDirection: 'row',
      alignItems: 'center',
      gap: s(10),
    },
    topBarRight: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: isPortraitSidebarMode ? s(8) : s(12),
      ...(isTabletCompact && { marginLeft: s(-3) }),
    },
    topBarLogoWrapper: {
      paddingVertical: v(2),
    },
    searchSlot: {
      flex: 1,
      maxWidth: isTabletCompact ? s(660) : s(620),
    },
    searchContainerExpanded: {
      backgroundColor: colors.surface,
      borderColor: colors.surface,
      borderWidth: 0,
    },
    topBarAction: {
      width: isPortraitSidebarMode ? s(34) : s(36),
      height: isPortraitSidebarMode ? v(34) : v(36),
      borderRadius: theme.borderRadius.md,
      alignItems: 'center',
      justifyContent: 'center',
      backgroundColor: 'rgba(255, 255, 255, 0.15)',
    },
    topBarActionHovered: {
      backgroundColor: 'rgba(255, 255, 255, 0.25)',
    },
    networkIndicator: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: isPortraitSidebarMode ? s(4) : s(5),
      paddingHorizontal: isPortraitSidebarMode ? s(8) : s(10),
      paddingVertical: isPortraitSidebarMode ? v(5) : v(6),
      borderRadius: theme.borderRadius.round,
    },
    networkOnline: {
      backgroundColor: 'rgba(255, 255, 255, 0.15)',
    },
    networkOffline: {
      backgroundColor: 'rgba(255, 100, 100, 0.25)',
    },
    networkText: {
      fontFamily: GTFonts.semibold,
      fontSize: fs(12),
      color: colors.textInverse,
    },
    dateTimeContainer: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: s(5),
      paddingHorizontal: s(6),
      display: isLarge ? 'flex' : 'none',
    } as any,
    dateTimeText: {
      fontFamily: GTFonts.regular,
      fontSize: fs(13),
      color: 'rgba(255, 255, 255, 0.85)',
      fontVariant: ['tabular-nums'],
    },
    // Keep time from shifting the top bar as seconds tick.
    timeText: {
      width: s(106),
    },

    categoriesSection: {
      flexDirection: 'row',
      alignItems: 'center',
      backgroundColor: colors.surface,
      paddingVertical: s(10),
      borderBottomWidth: borderWidth(1),
      borderBottomColor: colors.border,
      gap: s(8),
    },
    categoryToggle: {
      marginLeft: s(16),
      width: s(36),
      height: v(36),
      borderRadius: theme.borderRadius.md,
      alignItems: 'center',
      justifyContent: 'center',
      backgroundColor: colors.surfaceSecondary,
      borderWidth: borderWidth(1),
      borderColor: colors.border,
    },
    categoryToggleHovered: {
      backgroundColor: colors.border,
    },
    categoriesContent: {
      paddingRight: s(20),
      gap: s(8),
    },
    categoryPill: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: s(6),
      paddingHorizontal: s(12),
      paddingVertical: s(8),
      borderRadius: theme.borderRadius.md,
      borderWidth: borderWidth(1.5),
      borderColor: theme.colors.border,
      backgroundColor: colors.surface,
    },
    categoryPillActive: {
      borderColor: theme.colors.primary,
      backgroundColor: theme.colors.primary,
    },
    categoryPillHovered: {
      borderColor: colors.textSecondary,
      backgroundColor: colors.surfaceSecondary,
    },
    categoryLabel: {
      fontFamily: GTFonts.semibold,
      fontSize: fs(13),
      lineHeight: fs(16),
      color: colors.text,
      ...(Platform.OS === 'android' ? { includeFontPadding: false } : {}),
    },
    categoryLabelActive: {
      color: theme.colors.textInverse,
    },
    categoryCountBadge: {
      minWidth: s(22),
      height: v(22),
      borderRadius: theme.borderRadius.round,
      backgroundColor: colors.surfaceSecondary,
      alignItems: 'center',
      justifyContent: 'center',
      paddingHorizontal: s(5),
    },
    categoryCountBadgeActive: {
      backgroundColor: theme.colors.surface,
    },
    categoryCount: {
      fontFamily: GTFonts.semibold,
      fontSize: fs(11),
      lineHeight: fs(13),
      color: colors.textSecondary,
      ...(Platform.OS === 'android' ? { includeFontPadding: false } : {}),
    },
    categoryCountActive: {
      color: theme.colors.primary,
    },

    productsArea: {
      flex: 1,
      backgroundColor: colors.backgroundGray100,
    },
    gridContent: {
      paddingTop: v(10),
      paddingBottom: s(80),
      paddingHorizontal: s(2),
    },
    productItemWrapper: {
      paddingHorizontal: s(4),
      paddingBottom: s(8),
    },
    productItemHovered: {
      opacity: 0.95,
    },

    centerState: {
      flex: 1,
      alignItems: 'center',
      justifyContent: 'center',
    },
    errorText: {
      fontFamily: GTFonts.regular,
      color: theme.colors.error,
      fontSize: fs(14),
    },
    emptyContainer: {
      flex: 1,
      alignItems: 'center',
      justifyContent: 'center',
      paddingVertical: s(80),
    },
    emptyText: {
      fontFamily: GTFonts.semibold,
      fontSize: fs(18),
      lineHeight: fs(22),
      color: colors.text,
      marginTop: v(16),
    },
    emptySubtext: {
      fontFamily: GTFonts.regular,
      fontSize: fs(14),
      lineHeight: fs(20),
      color: colors.textSecondary,
      marginTop: v(8),
    },

    loadingFooter: {
      paddingVertical: s(24),
      alignItems: 'center',
      justifyContent: 'center',
    },

    floatingCartBtn: {
      position: 'absolute',
      bottom: v(24),
      right: s(24),
      width: s(60),
      height: v(60),
      borderRadius: theme.borderRadius.md,
      backgroundColor: colors.success,
      alignItems: 'center',
      justifyContent: 'center',
    } as any,
    floatingCartBtnPressed: {
      transform: [{ scale: 0.95 }],
    },
    floatingCartBadge: {
      position: 'absolute',
      top: -v(4),
      right: -s(4),
      minWidth: s(24),
      height: v(24),
      borderRadius: theme.borderRadius.round,
      backgroundColor: colors.secondary,
      alignItems: 'center',
      justifyContent: 'center',
      paddingHorizontal: s(6),
      borderWidth: borderWidth(2),
      borderColor: colors.surface,
    },
    floatingCartBadgeText: {
      fontFamily: GTFonts.bold,
      fontSize: fs(12),
      color: colors.surface,
    },

    bottomSheetTitle: {
      fontFamily: GTFonts.semibold,
      fontSize: fs(16),
      paddingVertical: s(8),
    },
    pickerBody: {
      paddingHorizontal: s(12),
      paddingBottom: s(12),
    },
    pickerListContent: {
      paddingBottom: s(18),
    },
    pickerRow: {
      justifyContent: 'space-between',
      gap: s(10),
    },
    pickerItem: {
      flex: 1,
      flexDirection: 'row',
      alignItems: 'center',
      gap: s(8),
      borderWidth: borderWidth(1),
      borderColor: colors.border,
      borderRadius: theme.borderRadius.md,
      paddingHorizontal: s(10),
      paddingVertical: s(10),
      marginBottom: v(10),
      backgroundColor: colors.surface,
    },
    pickerItemActive: {
      borderColor: colors.success,
      backgroundColor: colors.successLight,
    },
    pickerLabel: {
      fontFamily: GTFonts.semibold,
      color: colors.text,
      fontSize: fs(12),
      flex: 1,
    },
    pickerLabelActive: {
      color: colors.success,
    },
  });
};
